/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { Navigate, Outlet } from 'react-router-dom'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { useTokenRefresh } from '@/hooks/useTokenRefresh'
import { IdleWarningDialog } from './IdleWarningDialog'

interface ProtectedRouteProps {
  requireModule?: string
  requireFunction?: string
}

/**
 * Komponenta pro ochranu routes
 * - Vyžaduje přihlášení
 * - Volitelně kontroluje oprávnění k modulu/funkci
 * - Automaticky obnovuje token a sleduje aktivitu uživatele
 * 
 * @example
 * // Pouze přihlášení
 * <Route element={<ProtectedRoute />}>
 * 
 * @example
 * // S kontrolou oprávnění
 * <Route element={<ProtectedRoute requireModule="admin" requireFunction="manage_users" />}>
 */
export function ProtectedRoute({ requireModule, requireFunction }: ProtectedRouteProps = {}) {
  const { isAuthenticated } = useAuth()
  const { canAccess } = usePermissions()
  const { showIdleDialog, handleStayLoggedIn, handleLogout } = useTokenRefresh()

  // Kontrola přihlášení
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Kontrola oprávnění (pokud je vyžadováno)
  if (requireModule && requireFunction) {
    if (!canAccess(requireModule, requireFunction)) {
      // Přesměrování na dashboard pokud nemá oprávnění
      return <Navigate to="/dashboard" replace />
    }
  }

  return (
    <>
      <Outlet />
      <IdleWarningDialog
        open={showIdleDialog}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleLogout}
      />
    </>
  )
}

